import React, { useEffect, useState } from 'react';
import { Building, Users, Settings, ChevronDown } from 'lucide-react';
export type UserRole = 'mga_user' | 'captive_manager' | 'platform_admin';
interface Role {
  id: UserRole;
  name: string;
  description: string;
  icon: typeof Building;
}
const roles: Role[] = [{
  id: 'mga_user',
  name: 'MGA User',
  description: 'Managing General Agent view',
  icon: Building
}, {
  id: 'captive_manager',
  name: 'Captive Manager',
  description: 'Multi-MGA portfolio view',
  icon: Users
}, {
  id: 'platform_admin',
  name: 'Platform Admin',
  description: 'System administrator view',
  icon: Settings
}];
interface RoleSwitcherProps {
  onRoleChange?: (role: UserRole) => void;
}
export function RoleSwitcher({
  onRoleChange
}: RoleSwitcherProps) {
  const [selectedRole, setSelectedRole] = useState<UserRole>(() => {
    const stored = localStorage.getItem('demo_role');
    return stored as UserRole || 'mga_user';
  });
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    localStorage.setItem('demo_role', selectedRole);
    // Dispatch custom event so same-tab listeners (like useCurrentRole) update
    window.dispatchEvent(new CustomEvent('demo_role_change', {
      detail: selectedRole
    }));
    if (onRoleChange) {
      onRoleChange(selectedRole);
    }
  }, [selectedRole, onRoleChange]);
  const currentRole = roles.find(r => r.id === selectedRole)!;
  const Icon = currentRole.icon;
  return <div className="relative" data-id="element-2099">
      {/* Demo Mode Badge */}
      <div className="fixed top-4 right-4 z-50 bg-yellow-500 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg" data-id="element-2100">
        DEMO MODE
      </div>

      {/* Role Selector */}
      <div className="relative inline-block text-left" data-id="element-2101">
        <button onClick={() => setIsOpen(!isOpen)} className="inline-flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 transition-colors" data-id="element-2102">
          <Icon className="w-5 h-5 text-gray-600" data-id="element-2103" />
          <div className="text-left" data-id="element-2104">
            <div className="text-sm font-medium text-gray-900" data-id="element-2105">
              {currentRole.name}
            </div>
            <div className="text-xs text-gray-500" data-id="element-2106">
              {currentRole.description}
            </div>
          </div>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-2107" />
        </button>

        {/* Dropdown */}
        {isOpen && <>
            {/* Backdrop */}
            <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} data-id="element-2108" />

            {/* Menu */}
            <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-200 z-20" data-id="element-2109">
              <div className="p-2" data-id="element-2110">
                {roles.map(role => {
              const RoleIcon = role.icon;
              const isSelected = role.id === selectedRole;
              return <button key={role.id} onClick={() => {
                setSelectedRole(role.id);
                setIsOpen(false);
              }} className={`
                        w-full flex items-center gap-3 px-4 py-3 rounded-md transition-colors
                        ${isSelected ? 'bg-blue-50 border-2 border-blue-600' : 'hover:bg-gray-50 border-2 border-transparent'}
                      `} data-id="element-2111">
                      <RoleIcon className={`w-5 h-5 ${isSelected ? 'text-blue-600' : 'text-gray-600'}`} data-id="element-2112" />
                      <div className="text-left flex-1" data-id="element-2113">
                        <div className={`text-sm font-medium ${isSelected ? 'text-blue-900' : 'text-gray-900'}`} data-id="element-2114">
                          {role.name}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-2115">
                          {role.description}
                        </div>
                      </div>
                      {isSelected && <div className="w-2 h-2 bg-blue-600 rounded-full" data-id="element-2116" />}
                    </button>;
            })}
              </div>

              {/* Info */}
              <div className="p-4 bg-gray-50 border-t border-gray-200 rounded-b-lg" data-id="element-2117">
                <p className="text-xs text-gray-600" data-id="element-2118">
                  <strong data-id="element-2119">Note:</strong> This is a demo mode feature. In
                  production, roles are assigned by administrators.
                </p>
              </div>
            </div>
          </>}
      </div>
    </div>;
}
export function useCurrentRole(): UserRole {
  const [role, setRole] = useState<UserRole>(() => {
    const stored = localStorage.getItem('demo_role');
    return stored as UserRole || 'mga_user';
  });
  useEffect(() => {
    // Listen for cross-tab changes
    const handleStorageChange = () => {
      const stored = localStorage.getItem('demo_role');
      setRole(stored as UserRole || 'mga_user');
    };
    // Listen for same-tab changes via custom event
    const handleRoleChange = (e: Event) => {
      const customEvent = e as CustomEvent;
      setRole(customEvent.detail as UserRole || 'mga_user');
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('demo_role_change', handleRoleChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('demo_role_change', handleRoleChange);
    };
  }, []);
  return role;
}